import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, ImageIcon, ImageOff, Loader2 } from 'lucide-react';

import { KnowledgeParticleQaSections } from '../../components/KnowledgeParticleQaSections';
import type { KnowledgeParticle } from '../../types/domain';
import { TtqibEmbeddedImage } from './TtqibEmbeddedImage';
import { useTtqWorkbenchParticle } from './useTtqWorkbenchParticle';
import { useParticleEmbeddedImage, type ParticleSourceImage } from './useParticleEmbeddedImage';

export type TtqWorkbenchSourceParticleCardProps = {
  particleId?: string;
  particle?: KnowledgeParticle | null;
  title?: string;
  sourceImage?: ParticleSourceImage | null;
  imageLoading?: boolean;
  particleLoading?: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  explosionSlot?: ReactNode;
  footerSlot?: ReactNode;
  className?: string;
  indexLabel?: string;
};

function resolveFullSourceUrl(sourceImage?: ParticleSourceImage | null): string | undefined {
  return sourceImage?.fullSourceUrl || sourceImage?.crop?.sourceImageUrl || undefined;
}

/** 单颗源知识颗粒卡：整体可收起/展开 */
export function TtqWorkbenchSourceParticleCard({
  particleId,
  particle,
  title,
  sourceImage: sourceImageProp,
  imageLoading: imageLoadingProp,
  particleLoading,
  expanded,
  onToggleExpanded,
  explosionSlot,
  footerSlot,
  className = '',
  indexLabel,
}: TtqWorkbenchSourceParticleCardProps) {
  const [sourceOpen, setSourceOpen] = useState(false);
  const resolvedParticleId = particleId || particle?.id;
  const shouldLoadImage = Boolean(resolvedParticleId && expanded);
  const { data: loadedSourceImage, loading: loadedImageLoading } = useParticleEmbeddedImage(
    resolvedParticleId,
    shouldLoadImage,
  );
  const sourceImage = sourceImageProp ?? loadedSourceImage;
  const imageLoading = Boolean(imageLoadingProp || loadedImageLoading);
  const displayTitle = title || particle?.title || '源知识颗粒';

  const hasCrop = Boolean(sourceImage?.crop);
  const fullSourceUrl = resolveFullSourceUrl(sourceImage);
  const canShowFullSource = Boolean(fullSourceUrl);
  const showImageToggle = expanded && (imageLoading || canShowFullSource);
  const showInitialLoading = expanded && particleLoading && !particle;

  useEffect(() => {
    if (!expanded) setSourceOpen(false);
  }, [expanded]);

  return (
    <section
      className={
        'overflow-hidden rounded-xl border-2 border-green-200 bg-gradient-to-br from-green-50/90 to-surface shadow-sm '
        + className
      }
    >
      <div className="flex items-start justify-between gap-2 border-b border-green-100 bg-green-50/70 px-4 py-2.5">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left hover:opacity-80"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
        >
          {expanded ? <ChevronDown size={16} className="mt-0.5 shrink-0 text-green-700" /> : <ChevronRight size={16} className="mt-0.5 shrink-0 text-green-700" />}
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wide text-green-700">
              源知识颗粒{indexLabel ? ` · ${indexLabel}` : ''}
            </p>
            <p className="mt-0.5 truncate text-sm font-semibold text-ink">{displayTitle}</p>
          </div>
        </button>
        {showImageToggle ? (
          <button
            type="button"
            className={
              'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-xs '
              + (sourceOpen
                ? 'border-green-500 bg-green-50 text-green-700'
                : 'border-green-300 bg-white text-green-600 hover:bg-green-50')
            }
            aria-label={sourceOpen ? '收起原始素材' : '查看原始素材图'}
            aria-pressed={sourceOpen}
            title={imageLoading ? '加载原始素材…' : sourceOpen ? '收起原始素材' : '查看原始素材图'}
            onClick={() => {
              if (imageLoading) return;
              setSourceOpen((v) => !v);
            }}
          >
            {imageLoading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : sourceOpen ? (
              <ImageIcon size={14} />
            ) : (
              <ImageOff size={14} />
            )}
            <span className="whitespace-nowrap">{imageLoading ? '加载中…' : sourceOpen ? '收起原图' : '查看原始素材图'}</span>
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="space-y-3 p-4">
          {showInitialLoading ? (
            <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-green-200 bg-white/60">
              <Loader2 className="animate-spin text-green-600" size={18} />
            </div>
          ) : null}

          {!showInitialLoading && imageLoading && !hasCrop && !canShowFullSource ? (
            <div className="flex h-20 items-center justify-center rounded-lg border border-dashed border-green-200 bg-white/60 text-xs text-muted">
              <Loader2 className="mr-2 animate-spin" size={14} />
              加载原始素材…
            </div>
          ) : null}

          {!showInitialLoading && hasCrop ? (
            <div className="rounded-lg border border-green-100 bg-white p-2">
              <p className="mb-1 text-[10px] font-bold uppercase text-green-600">
                嵌入图
                <span className="ml-1 font-normal normal-case text-muted">· 点击放大查看</span>
              </p>
              <TtqibEmbeddedImage
                record={sourceImage?.crop}
                ossId={sourceImage?.ossId}
                height={160}
                width={300}
                zoomable
                zoomTitle="嵌入图"
              />
            </div>
          ) : null}

          {!showInitialLoading && sourceOpen && canShowFullSource ? (
            <div className="rounded-lg border border-dashed border-green-200 bg-white p-2">
              <p className="mb-1 text-[10px] font-bold uppercase text-muted">
                原始素材参考
                <span className="ml-1 font-normal normal-case text-muted">· 点击放大查看</span>
              </p>
              <TtqibEmbeddedImage
                fullSourceUrl={fullSourceUrl}
                ossId={sourceImage?.ossId}
                height={220}
                width={300}
                zoomable
                zoomTitle="原始素材"
              />
            </div>
          ) : null}

          {!showInitialLoading && particle ? (
            <KnowledgeParticleQaSections
              particle={particle}
              fallbackQuestionText={displayTitle}
              questionOverride={explosionSlot}
              questionEmptyText="暂无问题"
              answerEmptyText="暂无答案"
            />
          ) : null}

          {footerSlot}
        </div>
      ) : null}
    </section>
  );
}

export type TtqWorkbenchSourceParticleGroupItem = {
  particleId: string;
  title?: string;
};

export type TtqWorkbenchSourceParticleProps = Omit<
  TtqWorkbenchSourceParticleCardProps,
  'expanded' | 'onToggleExpanded'
> & {
  defaultExpanded?: boolean;
};

type TtqWorkbenchSourceParticleGroupProps = {
  items: TtqWorkbenchSourceParticleGroupItem[];
  activeParticleId?: string;
  onActiveParticleChange?: (particleId: string) => void;
  renderExplosion?: (ctx: { particleId: string; questionText: string }) => ReactNode;
};

function TtqWorkbenchSourceParticleGroupItem({
  item,
  expanded,
  onToggleExpanded,
  onActivate,
  indexLabel,
  isActive = false,
  renderExplosion,
}: {
  item: TtqWorkbenchSourceParticleGroupItem;
  expanded: boolean;
  onToggleExpanded: () => void;
  onActivate: () => void;
  indexLabel: string;
  isActive?: boolean;
  renderExplosion?: TtqWorkbenchSourceParticleGroupProps['renderExplosion'];
}) {
  const { particle, sourceImage, imageLoading, loading, questionText } = useTtqWorkbenchParticle(
    item.particleId,
    { enabled: expanded },
  );
  const explosionSlot = isActive && expanded && questionText && renderExplosion
    ? renderExplosion({ particleId: item.particleId, questionText })
    : undefined;

  return (
    <div onFocus={onActivate} onMouseEnter={onActivate}>
      <TtqWorkbenchSourceParticleCard
        particleId={item.particleId}
        particle={particle}
        title={item.title}
        sourceImage={sourceImage}
        imageLoading={imageLoading}
        particleLoading={loading}
        expanded={expanded}
        onToggleExpanded={() => {
          if (!expanded) onActivate();
          onToggleExpanded();
        }}
        explosionSlot={expanded ? explosionSlot : undefined}
        indexLabel={indexLabel}
      />
    </div>
  );
}

function buildDefaultExpandedIds(): Set<string> {
  return new Set();
}

/** 多颗源颗粒：默认全部收起，点击展开后再加载 */
export function TtqWorkbenchSourceParticleGroup({
  items,
  activeParticleId,
  onActiveParticleChange,
  renderExplosion,
}: TtqWorkbenchSourceParticleGroupProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(buildDefaultExpandedIds);

  const itemKey = useMemo(() => items.map((i) => i.particleId).join(','), [items]);
  const prevItemKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevItemKeyRef.current === null) {
      prevItemKeyRef.current = itemKey;
      return;
    }
    if (prevItemKeyRef.current !== itemKey) {
      prevItemKeyRef.current = itemKey;
      setExpandedIds(buildDefaultExpandedIds());
    }
  }, [itemKey]);

  const toggleExpanded = (particleId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(particleId)) next.delete(particleId);
      else next.add(particleId);
      return next;
    });
  };

  if (items.length === 0) return null;

  if (items.length === 1) {
    const item = items[0];
    return (
      <TtqWorkbenchSourceParticleGroupItem
        item={item}
        expanded={expandedIds.has(item.particleId)}
        onToggleExpanded={() => toggleExpanded(item.particleId)}
        onActivate={() => onActiveParticleChange?.(item.particleId)}
        indexLabel=""
        isActive
        renderExplosion={renderExplosion}
      />
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <TtqWorkbenchSourceParticleGroupItem
          key={item.particleId}
          item={item}
          expanded={expandedIds.has(item.particleId)}
          onToggleExpanded={() => toggleExpanded(item.particleId)}
          onActivate={() => onActiveParticleChange?.(item.particleId)}
          indexLabel={`${index + 1}/${items.length}`}
          isActive={item.particleId === activeParticleId}
          renderExplosion={renderExplosion}
        />
      ))}
    </div>
  );
}

/** 单颗快捷用法（等价于 Group 长度为 1） */
export function TtqWorkbenchSourceParticle(props: TtqWorkbenchSourceParticleProps) {
  const { defaultExpanded = true, ...cardProps } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <TtqWorkbenchSourceParticleCard
      {...cardProps}
      expanded={expanded}
      onToggleExpanded={() => setExpanded((v) => !v)}
    />
  );
}
